import Highcharts from 'highcharts';

export type ChartViewMode = 'chart' | 'table';
export type ChartRangeMode = 'all' | 'last12';

export interface FilterSelectionRequest {
    category?: string | null;
    seriesKey?: string | null;
    range?: ChartRangeMode;
}

export interface ChartSeriesVm {
    key: string;
    label: string;
    values: number[];
    color?: string;
}

export interface ChartData {
    categories: string[];
    series: ChartSeriesVm[];
}

export interface ChartCardOptions {
    rootSelector: string;
    chartSelector: string;
    timeToggleSelector?: string;
  
    initialRange?: ChartRangeMode;
    chartTitle?: string;
    //clear btn
    activeSelectionText?: string | null;
    onClearSelection?: () => void;
    borderTargetSelector?: string;
   
    onFilterSelect?: (request: FilterSelectionRequest) => void;
    onRangeChange?: (range: ChartRangeMode) => void;
}

export class ChartCard {
    private $root: JQuery;
  
    private $timeToggle: JQuery;

    private chart?: Highcharts.Chart;
    private chartData: ChartData = {
        categories: [],
        series: []
    };

   
    private rangeMode: ChartRangeMode;

    constructor(private options: ChartCardOptions) {
        this.$root = $(options.rootSelector);
        this.$timeToggle = this.$root.find(options.timeToggleSelector??'.js-time-toggle');
        this.rangeMode = options.initialRange ?? 'all';
    }

    init(): void {
        this.wireTimeToggle();
        this.wireClearButton();
        this.syncRangeButtons();
        this.renderClearButton();

    }

    setChartData(data: ChartData): void {
        this.chartData = data;
        this.renderLegend();
        this.renderChart();
        
    }

    setRange(mode: ChartRangeMode): void {
        this.rangeMode = mode;
        this.syncRangeButtons();
        this.renderChart();
        
    }

    setActiveSelection(text: string | null): void {
        this.options.activeSelectionText = text;
        this.renderClearButton();
    }

    private wireTimeToggle(): void {
        this.$timeToggle
            .off('click.chartRange')
            .on('click.chartRange', 'button', (e) => {
                const mode = String($(e.currentTarget).data('range') ?? '').trim() as ChartRangeMode;
                if (mode !== 'all' && mode !== 'last12') return;

                this.rangeMode = mode;
                this.syncRangeButtons();
                this.options.onRangeChange?.(mode);

                
            });
    }

  
    private syncRangeButtons(): void {
        this.$timeToggle.find('[data-range]').removeClass('active');
        this.$timeToggle.find(`[data-range="${this.rangeMode}"]`).addClass('active');
    }

    private renderChart(): void {
        const { categories, series } = this.chartData;
        const zeros = categories.map(() => 0);

        this.chart?.destroy();

        this.chart = Highcharts.chart(this.options.chartSelector.replace('#', ''), {
            chart: {
                type: 'bar',
                animation: false
            },
            title: { text: null as any },
            credits: { enabled: false },

            legend: {
                enabled: false // Disable default legend display
            },

            xAxis: {
                categories,
                title: { text: null as any },
                crosshair: true
            },

            yAxis: {
                min: 0,
                title: { text: 'Count' },
                allowDecimals: false,
                reversedStacks: false,
                stackLabels: {
                    enabled: true,
                    crop: false,
                    overflow: 'allow',
                    x: 4,
                    style: {
                  
                        color: '#333',
                        fontWeight: '600',
                        textOutline: 'none',
                    },
                    formatter: function () {
                        return String((this as any).total ?? '')
                    }
                   
                }

            },

            tooltip: {
                shared: true,
                useHTML: true,
                formatter: function () {
                    const points = this.points || [];
                    const center = this.x;

                    let html = `
            <div class="chart-tooltip">
                <div class="tooltip-title">View Details</div>
                <div class="tooltip-center">Center: ${center}</div>
        `;

                    points
                        .filter(p => Number(p.y ?? 0) > 0)
                        .forEach(p => {
                            const color = p.color;
                            const label = p.series.name;
                            const value = p.y;

                            html += `
            <div class="tooltip-row">
                <span class="tooltip-dot" style="background:${color}"></span>
                ${label} - ${value}
            </div>
        `;
                        });

                    html += `</div>`;
                    return html;
                }
            },


            plotOptions: {
                series: {
                    stacking: 'normal',
                   
                    cursor: 'pointer',
                  
                    states: {
                        inactive: {
                            opacity: 1
                        }
                    },
                    point: {
                        events: {
                            click: (e) => {
                                const point = e.point;
                                const category = String(point.category ?? '').trim();

                                this.options.onFilterSelect?.({
                                    category: category || null,
                                    range: this.rangeMode
                                });
                            }
                        }
                    }
                }
            },

            series: series.map(s => ({

                name: s.label,
                type: 'bar' as const,
                data: zeros,
                color: s.color,
                custom: { seriesKey: s.key }
            }))
        });

        setTimeout(() => {
            if (!this.chart) return;

            this.chart.reflow();

            series.forEach((s, i) => {
                this.chart!.series[i].setData(s.values, false, {
                    duration: 700,
                    easing: 'easeOutCubic'
                });
            });

            this.chart.redraw();
        }, 0);
    }
    private renderLegend(): void {
        const $legend = this.$root.find('.js-chart-legend');
        if (!$legend.length) return;

        $legend.empty();
        const visibleSeries = this.chartData.series.filter(s =>
            s.values.some(value => Number(value ?? 0) > 0)
        );

        visibleSeries.forEach(s => {
            const color = s.color ?? '#999';

            const item = `
            <span class="pub-chart-legend__item">
                <span class="pub-chart-legend__dot"
                      style="background-color:${color}"></span>
                ${s.label}
            </span>
        `;

            $legend.append(item);
        });
    }
    private wireClearButton(): void {
        this.$root
            .off('click.chartClearSelection', '.js-chart-clear')
            .on('click.chartClearSelection', '.js-chart-clear', () => {
                this.options.onClearSelection?.();
            });
    }

    private renderClearButton(): void {
        const $header = this.$root.find('.pub-chart-header');
        const $card = this.options.borderTargetSelector
            ? this.$root.closest(this.options.borderTargetSelector)
            : this.$root.closest('.col-60');

        if (!$header.length|| !$card.length) return;

        $header.find('.js-chart-clear').remove();

        const text = (this.options.activeSelectionText ?? '').trim();

        if (!text) {

            $card.removeClass('border-select');
            return;
        } 

        $card.addClass('border-select');

        $header.append(`
        <button type="button"
                class="clear-btn btn-sm js-chart-clear"
                title="Clear chart selection">
            Clear
        </button>
    `);
    }
}